import * as Dialog from '@radix-ui/react-dialog'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { useAuth } from '@/auth/AuthProvider'
import {
  FxButton,
  FxCreateTile,
  FxFolderCard,
  FxIcon,
  FxSegmentedTabs,
  FxSpinner,
  FxViewToggle,
} from '@/components/fx'
import { useCreateTemplateDialog } from '@/layouts/CreateTemplateDialog'
import { PageTitle, useShellHelp, useShellSearch } from '@/layouts/shellChrome'
import { foldersApi, templatesApi } from '@/lib/api'
import { useFavorites } from '@/lib/favorites'
import { cn } from '@/lib/utils'

import { FirstDay } from './FirstDay'
import { TemplateGridCard, TemplateListRow, templateHref } from './TemplateCardItem'
import { useTemplateCounts } from './useTemplateCounts'
import {
  OVERVIEW_TABS,
  filterTemplates,
  reviewBanner,
  type OverviewTab,
} from './overviewModel'

/**
 * Übersicht — the signed-in landing page (`Main.dc.html`,
 * `PhoneUebersicht.dc.html`, `StartUebersicht.dc.html`).
 *
 * The rail, the utility row, the search field and the help button belong to
 * `AppShell`; this screen claims the two it can honestly fill and lays out
 * its own content.
 *
 * Three things the artboards draw are not here, because nothing in the
 * gateway backs them and a control that does nothing is worse than no
 * control: the "Mit mir geteilt" tab (the template list carries no owner and
 * no shared flag), the lock badge (the caller's role is only obtainable per
 * template, one `/access` request per card) and the notification bell (there
 * is no feed endpoint of any kind).
 */

/** The gateway's maximum. One request covers all but the largest accounts. */
const PAGE_SIZE = 100

const VIEW_KEY = 'formalogix.overview.view'

function readView(): 'list' | 'grid' {
  try {
    return window.localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'grid'
  } catch {
    return 'grid'
  }
}

export function OverviewPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { favorites, toggle } = useFavorites()
  const [tab, setTab] = useState<OverviewTab>('recent')
  const [view, setView] = useState<'list' | 'grid'>(readView)
  const [helpOpen, setHelpOpen] = useState(false)

  const { query } = useShellSearch({ placeholder: 'Formulare durchsuchen' })
  useShellHelp({ onOpen: () => setHelpOpen(true) })

  const templates = useInfiniteQuery({
    // Not the bare `['templates']`: an infinite query caches
    // `{pages, pageParams}`, and the Projekte page reads the same key as a
    // plain array. See the note on `TEMPLATES_KEY` in pages/projekte.
    queryKey: ['templates', 'overview'],
    queryFn: ({ pageParam }) => templatesApi.list({ page: pageParam, pageSize: PAGE_SIZE }),
    initialPageParam: 1,
    // The list endpoint answers with a `total` the typed client does not
    // return, so "is there more" is read off the page's own length: a short
    // page is the last one.
    getNextPageParam: (last, all) => (last.length === PAGE_SIZE ? all.length + 1 : undefined),
    // What the Flutter overview polled for: a document finishing elsewhere,
    // or a form someone else added.
    refetchInterval: 30_000,
  })

  const folders = useQuery({ queryKey: ['folders'], queryFn: () => foldersApi.list() })

  const all = useMemo(() => templates.data?.pages.flat() ?? [], [templates.data])
  const ids = useMemo(() => all.map((t) => t.id), [all])
  const counts = useTemplateCounts(ids)

  const visible = useMemo(
    () => filterTemplates(all, { tab, query, favorites }),
    [all, tab, query, favorites],
  )

  // The banner is summed over every form that has been loaded, not only the
  // eight the "Zuletzt" tab shows, so switching tabs cannot change how much
  // work it claims is waiting.
  const banner = useMemo(
    () => reviewBanner(all.map((t) => ({ id: t.id, counts: counts.get(t.id) }))),
    [all, counts],
  )

  const setViewPersisted = (next: 'list' | 'grid') => {
    setView(next)
    try {
      window.localStorage.setItem(VIEW_KEY, next)
    } catch {
      // A remembered view is a convenience, not state worth failing over.
    }
  }

  /**
   * The create tile opens the **same** dialog as the shell's "+", rather than
   * a second way of creating a form. It used to navigate to `/onboarding` —
   * the pre-account funnel, which addresses a signed-in person as "du" and
   * asks for a filled form; see `layouts/CreateTemplateDialog`.
   */
  const onCreate = useCreateTemplateDialog().open

  if (templates.isPending) {
    return (
      <div className="flex flex-col gap-8">
        <PageTitle>{greeting(user?.firstName)}</PageTitle>
        <CardSkeletons />
      </div>
    )
  }

  if (templates.isError && all.length === 0) {
    return (
      <div className="flex flex-col gap-8">
        <PageTitle>{greeting(user?.firstName)}</PageTitle>
        <div className="flex max-w-[560px] flex-col items-start gap-4 rounded-sm bg-danger-subtle p-5">
          <b className="text-body-strong text-danger-text">
            Ihre Musterformulare konnten nicht geladen werden.
          </b>
          <span className="text-body text-text">
            Bitte prüfen Sie Ihre Verbindung und versuchen Sie es erneut.
          </span>
          <FxButton onClick={() => void templates.refetch()}>Erneut versuchen</FxButton>
        </div>
      </div>
    )
  }

  if (all.length === 0 && !query) {
    return (
      <>
        <FirstDay firstName={user?.firstName ?? ''} onCreate={onCreate} />
        <HelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
      </>
    )
  }

  return (
    <div className="flex flex-col gap-8 md:gap-10">
      <PageTitle>{greeting(user?.firstName)}</PageTitle>

      {banner && (
        <Link
          to={templateHref(banner.firstTemplateId)}
          className="flex flex-wrap items-center gap-4 rounded-sm bg-danger-subtle px-5 py-3.5 text-body text-text no-underline"
        >
          <FxIcon name="error" size={24} className="flex-none text-danger-text" />
          <span className="min-w-0 flex-1">{banner.text}</span>
          <span className="inline-flex h-control flex-none items-center gap-2 rounded-md bg-surface px-4 text-body-strong text-danger-text">
            Unklarheiten beheben
            <FxIcon name="arrow_forward" size={20} />
          </span>
        </Link>
      )}

      {/* Create tile and projects. On the phone the projects strip drops
          below the tile and scrolls sideways; the artboard replaces it with a
          single "Meine Projekte anzeigen" row, which is what the link at the
          end of the strip becomes when there is nothing before it. */}
      <section className="flex flex-col gap-8 md:flex-row md:items-stretch">
        <FxCreateTile className="md:w-[400px] md:flex-none" onClick={onCreate} />
        <div aria-hidden className="hidden w-0.5 flex-none bg-divider md:block" />
        <div className="flex min-w-0 flex-1 items-center gap-6 overflow-x-auto">
          {(folders.data ?? []).slice(0, 3).map((folder) => (
            // There is no per-project screen — the artboards' folder cards
            // are not links at all — so the card opens the Projekte page with
            // this project unfolded rather than dropping someone on a list
            // where nothing visibly happened.
            <FxFolderCard
              key={folder.id}
              className="flex-none"
              name={folder.name}
              onOpen={() => navigate(`/projekte?projekt=${encodeURIComponent(folder.id)}`)}
            />
          ))}
          <Link
            to="/projekte"
            className="flex flex-none items-center gap-3 px-1 text-tile-title text-text no-underline"
          >
            {folders.data?.length ? (
              <span>
                Weitere
                <br className="hidden md:inline" /> Projekte
              </span>
            ) : (
              <span>Meine Projekte anzeigen</span>
            )}
            <FxIcon name="arrow_forward" size={40} className="text-primary-text" />
          </Link>
        </div>
      </section>

      <hr className="m-0 border-0 border-t-2 border-divider" />

      <section className="flex flex-col gap-8 md:gap-10">
        <div className="flex flex-wrap items-center gap-8">
          <FxSegmentedTabs
          tabWidth={220}
            aria-label="Formulare filtern"
            className="min-w-0 flex-1"
            items={OVERVIEW_TABS}
            active={tab}
            onSelect={(value) => setTab(value as OverviewTab)}
          />
          <FxViewToggle value={view} onChange={setViewPersisted} />
        </div>

        {tab === 'favorites' && (
          // Said once, where the star is, rather than nowhere: there is no
          // favourite column in the gateway, so this really is per device.
          <p className="text-small text-text-muted">
            Favoriten werden nur auf diesem Gerät gespeichert.
          </p>
        )}

        {visible.length === 0 ? (
          <EmptyResult tab={tab} query={query} onCreate={onCreate} />
        ) : view === 'grid' ? (
          <div className="grid grid-cols-1 gap-8 xs:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 desktop:grid-cols-5">
            {visible.map((template) => (
              <TemplateGridCard
                key={template.id}
                template={template}
                counts={counts.get(template.id)}
                folders={folders.data ?? []}
                bookmarked={favorites.has(template.id)}
                onToggleBookmark={() => toggle(template.id)}
              />
            ))}
          </div>
        ) : (
          <ul className="flex list-none flex-col gap-2 p-0">
            {visible.map((template) => (
              <TemplateListRow
                key={template.id}
                template={template}
                counts={counts.get(template.id)}
                folders={folders.data ?? []}
                bookmarked={favorites.has(template.id)}
                onToggleBookmark={() => toggle(template.id)}
              />
            ))}
          </ul>
        )}

        {templates.hasNextPage && (tab !== 'recent' || query !== '') && (
          <div className="flex flex-col items-start gap-2">
            {tab !== 'recent' && (
              <FxButton
                variant="tonal"
                disabled={templates.isFetchingNextPage}
                onClick={() => void templates.fetchNextPage()}
              >
                {templates.isFetchingNextPage ? 'Wird geladen …' : 'Weitere Formulare laden'}
              </FxButton>
            )}
            {query && (
              // The gateway has no template search, so the filter can only see
              // what has been fetched. Saying so beats a box that quietly
              // finds nothing — on every tab, including "Zuletzt", where the
              // "Weitere laden" button has no place but the caveat does.
              <span className="text-small text-text-muted">
                Die Suche durchsucht die geladenen Formulare.
              </span>
            )}
          </div>
        )}
      </section>

      <HelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  )
}

function greeting(firstName: string | undefined): string {
  const name = firstName?.trim()
  return name ? `Hallo, ${name}!` : 'Hallo!'
}

function CardSkeletons() {
  return (
    <div
      className="grid grid-cols-1 gap-8 xs:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 desktop:grid-cols-5"
      aria-hidden
    >
      {Array.from({ length: 5 }, (_, i) => (
        <div key={i} className="h-[320px] rounded-sm bg-surface shadow-raised" />
      ))}
      <span className="sr-only">
        <FxSpinner label="Musterformulare werden geladen …" />
      </span>
    </div>
  )
}

function EmptyResult({
  tab,
  query,
  onCreate,
}: {
  tab: OverviewTab
  query: string
  onCreate: () => void
}) {
  if (query) {
    return (
      <p className="text-body text-text-muted">
        Unter den geladenen Musterformularen heißt keines „{query}“. Prüfen Sie die Schreibweise
        oder wechseln Sie den Tab.
      </p>
    )
  }
  if (tab === 'favorites') {
    return (
      <p className="text-body text-text-muted">
        Noch keine Favoriten. Das Lesezeichen auf einer Karte legt einen an.
      </p>
    )
  }
  return (
    <div className="flex flex-col items-start gap-4">
      <p className="text-body text-text-muted">Hier ist noch kein Musterformular.</p>
      <FxButton onClick={onCreate}>Musterformular hochladen</FxButton>
    </div>
  )
}

const HELP = [
  'Ein Musterformular ist die leere Vorlage: daraus lernt Formalogix, welche Felder gelesen werden sollen.',
  'Öffnen Sie ein Formular, um ausgefüllte Exemplare hinzuzufügen, auszuwerten und als Tabelle zu exportieren.',
  'Die Statuszeile auf einer Karte zählt die Dokumente dieses Formulars — „unklar“ heißt, dass Werte auf Ihre Prüfung warten.',
  'Favoriten und die Ansicht (Liste oder Raster) merkt sich dieser Browser, nicht Ihr Konto.',
]

function HelpDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Dialog.Root open={open} onOpenChange={(next) => !next && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-[#0000008c]" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(520px,calc(100vw-40px))] -translate-x-1/2 -translate-y-1/2 rounded-xl bg-surface p-5 shadow-overlay xs:p-8">
          <Dialog.Title className="mb-4 text-dialog-title text-text">Übersicht</Dialog.Title>
          <ul className="flex list-none flex-col gap-4 p-0">
            {HELP.map((hint) => (
              <li key={hint} className={cn('flex items-start gap-2 text-body text-text')}>
                <FxIcon name="check" size={20} className="mt-0.5 flex-none text-primary-text" />
                {hint}
              </li>
            ))}
          </ul>
          <div className="mt-8 flex justify-end">
            <FxButton onClick={onClose}>Schließen</FxButton>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
